import '../style.css';
import { Map, Overlay, View } from 'ol';
import { useGeographic } from "ol/proj";
import { Cluster, OSM } from "ol/source";
import VectorLayer from "ol/layer/Vector";
import MapboxVectorLayer from "ol/layer/MapboxVector";
import TileLayer from "ol/layer/Tile";
import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import VectorSource from "ol/source/Vector";
import { loadPoisForMapCoordinates } from "./poi";
import { startAlpine } from "./alpine";
import CircleStyle from "ol/style/Circle";
import Alpine from "alpinejs";
import { defaults as defaultControls } from 'ol/control.js';
import { Attribution } from "ol/control";

const popup = document.getElementById('popup');
const closer = document.getElementById('popup-closer');

const mapTilerKey = 'sxBc9HTJDVCkF6E0qOOu';
const useMaptiler = true;
let map;
let clusterSource;

const minClusterDistance = 40;
const maxClusterDistance = 180;

const minClusterRadius = 15;
const maxClusterRadius = 30;

const attribution = new Attribution({
    collapsible: true,
    collapsed: false,
    collapseClassName: 'attribution',
    expandClassName: 'attribution'
});

let debounceTimer;
const overlay = new Overlay({
    element: popup,
    autoPan: {
        animation: {
            duration: 500,
        },
        margin: 0,
    },
});
let selectedFeature;

startAlpine();
initMap(useMaptiler);
createOverlayPopup();
setBudapestAsMapCenter();

function checkSize() {
    const small = map.getSize()[0] < 600;
    attribution.setCollapsible(small);
    attribution.setCollapsed(small);
}

window.addEventListener('resize', checkSize);

function initMap(useMaptiler) {
    useGeographic();


    map = new Map({
        target: 'map',
        controls: defaultControls({ attribution: false }).extend([attribution]),
        overlays: [overlay],
        layers: [useMaptiler ? createMaptilerLayer() : createOsmLayer(), createClusterLayer()],
        view: new View({
            center: [0, 0],
            zoom: 2
        })
    });
    let lastZoom = map.getView().getZoom();
    map.on('moveend', () => {
        debounce(() => {
            const coordinates = map.getView().calculateExtent();
            loadPoisForMapCoordinates(coordinates[3], coordinates[2], coordinates[1], coordinates[0]);
        });
    });

    map.on('moveend', () => {
        const zoom = map?.getView().getZoom();
        if (zoom !== lastZoom) {
            lastZoom = zoom;
            clusterSource.setDistance(getClusterDistance(zoom));
        }
    });
}

function getClusterDistance(zoom) {
    if (zoom < 8) {
        return maxClusterDistance;
    } else if (zoom <= 14) {
        return maxClusterDistance - (zoom - 10) * (maxClusterDistance - minClusterDistance) / (14 - 10);
    } else {
        return minClusterDistance;
    }
}

function createClusterLayer() {
    window.window.iconSource = new VectorSource({
        features: [],
    });

    clusterSource = new Cluster({
        distance: 40,
        minDistance: minClusterDistance,
        source: window.iconSource,
    });

    const styleCache = {};
    return new VectorLayer({
        source: clusterSource,
        style: function (feature) {
            const features = feature.get('features');
            const size = features.length;
            let style = size === 1 ? undefined : styleCache[size];
            if (!style) {
                style = size === 1 ? createSingularPointStyle(features[0].get('label')) : createClusterStyle(size);
                styleCache[size] = style;
            }
            return style;
        }
    });
}

function createMaptilerLayer() {
    return new MapboxVectorLayer({
        styleUrl:
            `assets/style.json?key=${mapTilerKey}`,
    });
}

function createOsmLayer() {
    return new TileLayer({
        source: new OSM()
    });
}

function createClusterStyle(size) {
    return new Style({
        image: new CircleStyle({
            radius: size > minClusterRadius ? (size >= maxClusterRadius ? maxClusterRadius : size) : minClusterRadius,
            stroke: new Stroke({
                color: '#41580a80',
            }),
            fill: new Fill({
                color: '#FDE390',
            }),
        }),
        text: new Text({
            text: size.toString(),
            fill: new Fill({
                color: '#3E330D',
            }),
        }),
    });
}

function setBudapestAsMapCenter() {
    const budapestPosition = new View({ center: [19.049435, 47.495777], zoom: 13.724794638459691 });
    map.setView(budapestPosition);
}

function createSingularPointStyle(label) {
    let iconSrc
    let scale = 1;
    if (imageExists(label.toLowerCase())) {
        iconSrc = `assets/icons/${label.toLowerCase()}.svg`;
    } else {
        iconSrc = `assets/icons/butterfly.svg`;
        scale = 0.15;
    }
    return new Style({
        image: new Icon({
            anchor: [0.5, 0.5],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: iconSrc,
            scale: scale,
        }),
        fill: new Fill({
            color: '#E823CEFF',
        }),
    });
}

function createOverlayPopup() {
    closer.onclick = function () {
        addHiddenFeatureBackToMap();
        overlay.setPosition(undefined);
        closer.blur();
        return false;
    };

    map.on('singleclick', (evt) => {
        const features = map.forEachFeatureAtPixel(evt.pixel, function (feature) {
            const collection = feature.get('features');
            return collection?.length < 10 ? collection : null;
        });
        if (features) {
            let props = features.map(feat => feat.getProperties());
            props = deduplicate(props);

            Alpine.store('selectedPoi').set(props, props.length);
            overlay.setPosition([features[0].get('long'), features[0].get('lat')]);
            selectedFeature = features;
        } else {
            addHiddenFeatureBackToMap();
            overlay.setPosition(undefined);
            Alpine.store('selectedPoi').set(null);
        }
    })
}

function addHiddenFeatureBackToMap() {
    if (selectedFeature) {
        selectedFeature = null;
    }
}

function debounce(func, timeout = 50) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        func.apply(this);
    }, timeout);
}

function imageExists(src) {
    const img = new Image();
    img.src = `assets/icons/${src}.svg`;
    return img.height !== 0;
}

function deduplicate(features) {
    const unique = new Set();
    return features.filter(feat => {
        if (unique.has(feat.name)) {
            unique.add(`${feat.name}_${feat.subCategory}`);
            return false;
        }
        return true;
    })
}